import { useCallback, useEffect, useState } from "react";

type Config<T, Q> =
    | { method: "GET"; query?: Record<string, Q>; id?: string; endpoint?: string }
    | { method: "POST"; payload: T; query?: Record<string, unknown>; endpoint?: string }
    | { method: "UPDATE"; id: string; payload: T; query?: Record<string, unknown>; endpoint?: string }
    | { method: "DELETE"; id: string; query?: Record<string, unknown>; endpoint?: string };

type UseAPIResult<R> = {
    response: R | null;
    loading: boolean;
    error: string | null;
    trigger: <T, Q>(config: Config<T, Q>) => Promise<R | null>;
};


const buildQueryString = (query: Record<string, unknown> = {}): string => {
    const parts: string[] = [];

    const flatten = (
        obj: Record<string, unknown> | unknown[],
        prefix = ""
    ) => {
        if (Array.isArray(obj)) {
            obj.forEach((value) => {
                const key = `${prefix}[]`;
                if (typeof value === "object" && value !== null) {
                    flatten(value as Record<string, unknown>, key);
                } else {
                    parts.push(`${key}=${encodeURIComponent(String(value))}`);
                }
            });
        } else {
            Object.entries(obj).forEach(([key, value]) => {
                const fullKey = prefix
                    ? `${prefix}[${key}]`
                    : key;

                if (typeof value === "object" && value !== null && !Array.isArray(value)) {
                    flatten(value as Record<string, unknown>, fullKey);
                } else if (Array.isArray(value)) {
                    value.forEach((v) => {
                        parts.push(`${fullKey}[]=${encodeURIComponent(String(v))}`);
                    });
                } else {
                    parts.push(`${fullKey}=${encodeURIComponent(String(value))}`);
                }
            });
        }
    };

    flatten(query);

    return parts.join("&");
};


export const useAPI = <R = unknown, T = unknown, Q = unknown>(
    defaultEndpoint: string,
    initialCall: boolean = false,
    dependencies: unknown[] = [],
    defaultConfig?: Config<T, Q>
): UseAPIResult<R> => {

    const [response, setResponse] = useState<R | null>(null);
    const [loading, setLoading] = useState(() => initialCall ? true : false);
    const [error, setError] = useState<string | null>(null);

    const trigger = useCallback(async <T, Q>(config: Config<T, Q>): Promise<R | null> => {
        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const { method } = config;
            const payload = "payload" in config ? config.payload : undefined;
            const id = "id" in config ? config.id : undefined;
            const query = "query" in config ? config.query : undefined;


            const queryString = query ? buildQueryString(query) : "";

            const baseEndpoint = config.endpoint ?? defaultEndpoint;
            const url = `${import.meta.env.VITE_APP_API_URL}/${baseEndpoint}${id ? `/${id}` : ""}${queryString ? `?${queryString}` : ""}`;

            let res: Response;
            switch (method) {
                case "GET":
                    res = await fetch(url, { method: "GET", credentials: "include" });
                    break;
                case "POST":
                    res = await fetch(url, {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    });
                    break;
                case "UPDATE":
                    res = await fetch(url, {
                        method: "PUT",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    });
                    break;
                case "DELETE":
                    res = await fetch(url, {
                        credentials: "include",
                        method: "DELETE",
                    });
                    break;
                default:
                    throw new Error("Invalid method");
            }
            if (!res.ok) throw new Error(`Error: ${res.status}`);
            const data: R = await res.json();
            setResponse(data);

            return data;
        } catch (err) {

            setError((err as Error).message);

            return null;
        } finally {
            setLoading(false);
        }
    }, [defaultEndpoint]);

    useEffect(() => {
        if (initialCall && defaultConfig) {

            trigger(defaultConfig);
        }
    }, [defaultEndpoint, JSON.stringify(defaultConfig), trigger, ...dependencies]);

    return { response, loading, error, trigger };
};
